import React, { useState } from 'react';
import { Modal, Button, Input, Textarea, Toggle } from '../../components/ds';
import api from '../../api/client';

/**
 * Modal for creating a new PAC application under a company.
 *
 * Fields (AS-179 spec):
 *   - app_name (required)
 *   - app_ids: multi-line textarea, one domain per line — joined with \n into one PAC app_id
 *   - allow_pc: toggle (controls allow_non_mobile_requests)
 *   - integration_type: hardcode | gam
 *
 * Removed from original form: OS, publisher_tier, deal_type, IAB categories (all use defaults).
 */
export default function AddApplicationModal({ accountId, companyId, onClose, onAdded }) {
  const [form, setForm] = useState({
    app_name: '',
    app_ids: '',
    allow_pc: false,
    integration_type: 'hardcode',
  });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const handleFieldChange = (field) => (e) => {
    setForm((prev) => ({ ...prev, [field]: e.target.value }));
    if (error) setError('');
  };

  const handleToggleChange = (field) => (e) => {
    setForm((prev) => ({ ...prev, [field]: e.target.checked }));
  };

  const handleSubmit = async () => {
    if (!form.app_name.trim()) {
      setError('請填寫 Application 名稱');
      return;
    }
    const domains = form.app_ids
      .split('\n')
      .map((d) => d.trim())
      .filter(Boolean);
    if (domains.length === 0) {
      setError('請至少填寫一個 Domain');
      return;
    }

    setSubmitting(true);
    setError('');
    try {
      await api.createApplication(accountId, {
        company_id: companyId,
        app_name: form.app_name.trim(),
        app_ids: domains.join('\n'),
        allow_pc: form.allow_pc,
        integration_type: form.integration_type,
      });
      onAdded();
    } catch (err) {
      setError('建立 Application 失敗：' + err.message);
    } finally {
      setSubmitting(false);
    }
  };

  const canSubmit = !!form.app_name.trim() && !!form.app_ids.trim();

  const footer = (
    <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
      <Button variant="secondary" onClick={onClose} disabled={submitting}>
        取消
      </Button>
      <Button onClick={handleSubmit} disabled={!canSubmit || submitting}>
        {submitting ? '建立中...' : '建立'}
      </Button>
    </div>
  );

  return (
    <Modal title="新建 Application" onClose={onClose} footer={footer}>
      <Input
        label="Application 名稱 *"
        value={form.app_name}
        onChange={handleFieldChange('app_name')}
        placeholder="e.g. Acme News Site"
      />

      <div style={{ marginTop: 12 }}>
        <Textarea
          label="Domain (一行一個) *"
          value={form.app_ids}
          onChange={handleFieldChange('app_ids')}
          placeholder={'example.com\nwww.example.com'}
          rows={4}
        />
        <div style={{ fontSize: 12, color: 'var(--text-tertiary)', marginTop: 4 }}>
          多個 domain 每行填一個，會合併成同一個 Application。
        </div>
      </div>

      <div style={{ marginTop: 16 }}>
        <div style={{ fontSize: 13, fontWeight: 500, marginBottom: 6, color: 'var(--text-primary)' }}>
          整合類型
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          {['hardcode', 'gam'].map((t) => (
            <button
              key={t}
              type="button"
              onClick={() => setForm((prev) => ({ ...prev, integration_type: t }))}
              style={{
                flex: 1,
                padding: '8px 16px',
                borderRadius: 'var(--radius-md)',
                border: form.integration_type === t
                  ? '2px solid var(--primary-orange)'
                  : '1px solid var(--border-color)',
                background: form.integration_type === t ? 'var(--primary-orange-tint)' : 'transparent',
                color: form.integration_type === t ? 'var(--primary-orange)' : 'var(--text-secondary)',
                fontWeight: 600,
                fontSize: 14,
                cursor: 'pointer',
                transition: 'all 150ms ease',
              }}
            >
              {t === 'hardcode' ? 'Hardcode' : 'GAM'}
            </button>
          ))}
        </div>
      </div>

      <div style={{ marginTop: 16, display: 'flex', alignItems: 'flex-start', gap: 12 }}>
        <Toggle
          checked={form.allow_pc}
          onChange={handleToggleChange('allow_pc')}
        />
        <div>
          <div style={{ fontSize: 14, fontWeight: 500, color: 'var(--text-primary)', lineHeight: 1.4 }}>
            Allow PC
          </div>
          <div style={{ fontSize: 12, color: 'var(--text-tertiary)', marginTop: 2 }}>
            PAC 預設只收 mobile，需 PC 版位才啟用
          </div>
        </div>
      </div>

      {error && (
        <div style={{ marginTop: 12, color: 'var(--status-error-text)', fontSize: 13 }}>
          {error}
        </div>
      )}
    </Modal>
  );
}
